import { useState } from "react";
import type {
  StylePreset,
  Engine,
  ImageSize,
  ImageQuality,
  ImagePromptObject,
  QuoteInput as QuoteInputType,
} from "../types";
import { parseQuotes } from "../lib/quoteParser";
import { buildPrompts } from "../lib/api";

interface Props {
  stylePreset: StylePreset | "auto";
  setStylePreset: (v: StylePreset | "auto") => void;
  withText: boolean;
  setWithText: (v: boolean) => void;
  noMockup: boolean;
  setNoMockup: (v: boolean) => void;
  engine: Engine;
  setEngine: (v: Engine) => void;
  size: ImageSize;
  setSize: (v: ImageSize) => void;
  quality: ImageQuality;
  setQuality: (v: ImageQuality) => void;
  onPromptsBuilt: (prompts: ImagePromptObject[]) => void;
  isBuilding: boolean;
  setIsBuilding: (v: boolean) => void;
  setError: (e: string | null) => void;
}

export function QuoteInput({
  stylePreset,
  setStylePreset,
  withText,
  setWithText,
  noMockup,
  setNoMockup,
  engine,
  setEngine,
  size,
  setSize,
  quality,
  setQuality,
  onPromptsBuilt,
  isBuilding,
  setIsBuilding,
  setError,
}: Props) {
  const [inputText, setInputText] = useState("");

  const handleBuildPrompts = async () => {
    if (!inputText.trim()) {
      setError("Please enter at least one quote");
      return;
    }

    setIsBuilding(true);
    setError(null);

    try {
      // Parse quotes from input
      const parsed = parseQuotes(inputText);

      // Convert to QuoteInput format
      const quotes: QuoteInputType[] = parsed.map((q, i) => ({
        id: `quote_${Date.now()}_${i}`,
        text: q.text,
        author: q.author,
      }));

      // Build prompts via API
      const result = await buildPrompts(quotes, {
        stylePreset: stylePreset === "auto" ? undefined : stylePreset,
        withText,
        mockup: !noMockup,
        engine,
      });

      onPromptsBuilt(result.prompts);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to build prompts");
    } finally {
      setIsBuilding(false);
    }
  };

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
      <h2 className="text-lg font-semibold mb-4">Enter Quotes</h2>

      <textarea
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        placeholder={`Enter quotes, one per line:

"The only way to do great work is to love what you do." — Steve Jobs
"Not all those who wander are lost." — J.R.R. Tolkien
"Be the change you wish to see in the world." — Gandhi`}
        className="w-full h-48 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      />

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Style Preset */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Style Preset
          </label>
          <select
            value={stylePreset}
            onChange={(e) =>
              setStylePreset(e.target.value as StylePreset | "auto")
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="auto">Auto (detect from quote)</option>
            <option value="watercolor-travel">Watercolor Travel</option>
            <option value="fantasy-circular">Fantasy Circular</option>
            <option value="minimal-apple">Minimal Apple</option>
            <option value="fashion-ink-gold">Fashion Ink & Gold</option>
            <option value="neon-time">Neon Time</option>
            <option value="generic">Generic</option>
          </select>
        </div>

        {/* Engine */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Engine
          </label>
          <select
            value={engine}
            onChange={(e) => setEngine(e.target.value as Engine)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="openai">OpenAI (DALL·E 3)</option>
            <option value="workers-ai">Workers AI (Flux)</option>
          </select>
        </div>

        {/* Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Image Size
          </label>
          <select
            value={size}
            onChange={(e) => setSize(e.target.value as ImageSize)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="1024x1024">1024×1024 (Square)</option>
            <option value="1024x1792">1024×1792 (Portrait)</option>
            <option value="1792x1024">1792×1024 (Landscape)</option>
          </select>
        </div>

        {/* Quality */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Quality
          </label>
          <select
            value={quality}
            onChange={(e) => setQuality(e.target.value as ImageQuality)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="standard">Standard</option>
            <option value="hd">HD</option>
          </select>
        </div>

        {/* With Text Toggle */}
        <div className="flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={withText}
              onChange={(e) => setWithText(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              Include text in artwork
            </span>
          </label>
        </div>

        {/* No Mockup Toggle */}
        <div className="flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={noMockup}
              onChange={(e) => setNoMockup(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              No mockup (art only)
            </span>
          </label>
        </div>
      </div>

      <div className="mt-6">
        <button
          onClick={handleBuildPrompts}
          disabled={isBuilding || !inputText.trim()}
          className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {isBuilding ? "Building Prompts..." : "Build Prompts"}
        </button>
      </div>
    </section>
  );
}
